import { cn } from "@/lib/utils";
import type { ObjMetric } from "../metrics";
import { fmtMetric } from "../metrics";
import { magnitudePct, positionPct, zoomedDomain } from "./chart-scale";
import { WINNER_COLOR } from "./IndicatorCompare";

interface HeroComparisonProps {
  base: ObjMetric | null;
  winner: ObjMetric | null;
  /** Texto do ganho, já com sinal e direção resolvidos pelo registry (`objDelta`). */
  delta: { text: string; positive: boolean } | null;
}

/**
 * O ganho do vencedor sobre a configuração atual, desenhado ao lado do número grande.
 *
 * O hero dizia "1h28" e "−10 min vs base": dois números que o leitor tinha de subtrair de cabeça
 * para enxergar o tamanho do ganho. Aqui a comparação é a própria imagem.
 *
 * A forma DEPENDE da grandeza, e é isto que impede o gráfico de mentir:
 * - tempo e produção têm zero real e nunca são negativos aqui ⇒ **barras ancoradas no zero**, onde
 *   a proporção entre elas significa alguma coisa;
 * - OEE (e folga, que pode ser negativa) ⇒ **trilho recortado com duas marcas**, porque 93,8 e
 *   94,4 numa barra de 0 a 100 são a mesma barra, e barra negativa não existe.
 */
export function HeroComparison({ base, winner, delta }: HeroComparisonProps) {
  if (!base || !winner || base.value == null || winner.value == null) return null;

  const zeroAnchored = base.unit !== "%" && base.value >= 0 && winner.value >= 0;
  const max = Math.max(base.value, winner.value) * 1.05;
  const domain = zoomedDomain([base.value, winner.value]);

  const rows: Array<{ label: string; m: ObjMetric; color?: string }> = [
    { label: "Hoje", m: base },
    { label: "Recomendado", m: winner, color: WINNER_COLOR },
  ];

  return (
    <div
      className="min-w-[260px] max-w-[460px] flex-1"
      role="img"
      aria-label={`Hoje ${fmtMetric(base)}, recomendado ${fmtMetric(winner)}${
        delta ? `, ${delta.text}` : ""
      }`}
    >
      {rows.map(({ label, m, color }) => (
        <div key={label} className="mb-1 flex items-center gap-2">
          <span className="w-[74px] shrink-0 text-[10px] text-muted-foreground">{label}</span>
          <span className="relative h-2.5 flex-1 overflow-hidden rounded-full bg-bg-sunken">
            {zeroAnchored ? (
              <span
                className={cn(
                  "absolute inset-y-0 left-0 w-[var(--hb-w)] rounded-full",
                  color ? "bg-[var(--hb-c)]" : "bg-text-secondary"
                )}
                style={
                  {
                    "--hb-w": `${magnitudePct(m.value ?? 0, max)}%`,
                    ...(color ? { "--hb-c": color } : {}),
                  } as React.CSSProperties
                }
              />
            ) : (
              <span
                className={cn(
                  "absolute left-[var(--hb-x)] top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-bg-surface",
                  color ? "bg-[var(--hb-c)]" : "border-2 border-text-secondary bg-bg-surface"
                )}
                style={
                  {
                    "--hb-x": `${positionPct(m.value ?? 0, domain)}%`,
                    ...(color ? { "--hb-c": color } : {}),
                  } as React.CSSProperties
                }
              />
            )}
          </span>
          <span className="w-14 shrink-0 text-right font-mono text-[11px] tabular-nums">
            {fmtMetric(m)}
          </span>
        </div>
      ))}

      <p className="pl-[82px] text-[10px] text-muted-foreground">
        {zeroAnchored ? "eixo começa em zero" : "eixo recortado na faixa observada"}
        {delta ? (
          <>
            {" · "}
            <b className={delta.positive ? "text-rd-success-500" : "text-rd-danger-500"}>
              {delta.text}
            </b>
          </>
        ) : null}
      </p>
    </div>
  );
}
